/**
 * The bills module's ONE enum → plain-words map (POLISH v1 rule 2: "plain words everywhere —
 * 'Voting open', not OPEN"; rule 3: meaning carried twice, by colour AND words together).
 *
 * Before this existed, six screens each rendered `{bill.status}` raw, so a client saw the literal
 * database value — "PROPOSAL", "CANCELED" — in a badge. The words live HERE rather than in the kit's
 * `GovStatusPill`, because that pill carries *governance's* vocabulary and the kit is a copy shared
 * by several modules: a module with its own statuses supplies its own map and passes the result in
 * as `label`/`tone` (see the note at the top of `components/ui/GovStatusPill.tsx`).
 *
 * An unknown value is returned as-is rather than hidden or blanked — a status nobody mapped is a
 * thing to notice, not a thing to swallow.
 */

export type StatusTone = "accent" | "success" | "warning" | "danger" | "neutral" | "purple";

/** A bill's own lifecycle, in the words a client would use. */
const BILL_WORDS: Record<string, string> = {
    DRAFT: "Draft",
    PROPOSAL: "Proposal",
    SENT: "Sent",
    PAID: "Paid",
    OVERDUE: "Overdue",
    CANCELED: "Cancelled",
    CANCELLED: "Cancelled",
    VOID: "Voided",
};

const BILL_TONE: Record<string, StatusTone> = {
    DRAFT: "neutral",
    PROPOSAL: "purple",
    SENT: "accent",
    PAID: "success",
    OVERDUE: "danger",
    CANCELED: "neutral",
    CANCELLED: "neutral",
    VOID: "neutral",
};

/** A subscription's billing lifecycle. */
const SUBSCRIPTION_WORDS: Record<string, string> = {
    ACTIVE: "Active",
    PAUSED: "Paused",
    CANCELED: "Cancelled",
    CANCELLED: "Cancelled",
    EXPIRED: "Expired",
    PENDING: "Pending",
    TRIALING: "On trial",
    PAST_DUE: "Payment overdue",
    INCOMPLETE: "Payment incomplete",
};

const SUBSCRIPTION_TONE: Record<string, StatusTone> = {
    ACTIVE: "success",
    PAUSED: "warning",
    CANCELED: "neutral",
    CANCELLED: "neutral",
    EXPIRED: "neutral",
    PENDING: "warning",
    TRIALING: "accent",
    PAST_DUE: "danger",
    INCOMPLETE: "warning",
};

export const billStatusWords = (status?: string | null): string =>
    (status && BILL_WORDS[status]) || status || "Unknown";

export const billStatusTone = (status?: string | null): StatusTone =>
    (status && BILL_TONE[status]) || "neutral";

export const subscriptionStatusWords = (status?: string | null): string =>
    (status && SUBSCRIPTION_WORDS[status]) || status || "Unknown";

export const subscriptionStatusTone = (status?: string | null): StatusTone =>
    (status && SUBSCRIPTION_TONE[status]) || "neutral";

/**
 * How a bill is being paid, in the words a client would use.
 *
 * The PUBLIC bill page showed a logged-out client "Payment Method: AUD_TRANSFER" — the most
 * client-facing string in the module, and a database value. `PaymentProvider` in the generated
 * schema is the full set.
 */
const PAYMENT_WORDS: Record<string, string> = {
    AUD_TRANSFER: "Bank transfer (AUD)",
    BANK_TRANSFER: "Bank transfer",
    BB_TOKEN: "Business Builder tokens",
    CASH: "Cash",
    CRYPTO: "Cryptocurrency",
    STRIPE: "Card",
    CARD: "Card",
    BTC: "Bitcoin",
    OTHER: "Other",
};

export const paymentMethodWords = (method?: string | null): string =>
    (method && PAYMENT_WORDS[method]) || method || "Not specified";

/**
 * A plan tier, in words. The tier is DERIVED from the product slug by uppercasing it
 * (`deriveTier` in SubscriptionProducts), so it is a machine key rather than an enum with a fixed
 * set — "GROWTH_PLUS" was rendered to the reader exactly as stored. Formatting it is the fix;
 * a lookup table would go stale the moment someone adds a plan.
 */
export const tierWords = (tier?: string | null): string =>
    tier ? tier.toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "—";

/**
 * A bill's payment state on the subscription's billing history. It was rendered as the stored
 * value with its underscores swapped for spaces — "AWAITING PAYMENT" is still the database
 * talking, just with better spacing.
 */
const PAYMENT_STATE_WORDS: Record<string, string> = {
    PAID: "Paid",
    OVERDUE: "Overdue",
    AWAITING_PAYMENT: "Awaiting payment",
    PARTIALLY_PAID: "Part paid",
    DRAFT: "Draft",
    PROPOSAL: "Proposal",
    CANCELLED: "Cancelled",
    CANCELED: "Cancelled",
};

export const billPaymentStateWords = (state?: string | null): string =>
    (state && PAYMENT_STATE_WORDS[state]) || state || "Unknown";
